import React, { useEffect, useState } from 'react';
import { Drawer, Input, InputNumber, Modal, Radio, Select, Table } from 'antd';
import { Plus } from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  RewardInput,
  SavePlatformChallengeBody,
  cancelDonateEvent,
  createDonateEvent,
  getDonateEvent,
  listDonateEvents,
  retryDonateEventRewards,
  updateDonateEvent,
} from '../../../../api/admin/donate-event.api';
import { getGrantableItems } from '../../../../api/admin/cosmetic.api';
import { StoreItem } from '../../../../api/payment/store.api';
import { ChallengeMode, DonateChallenge } from '../../../../api/payment/donate-challenge.api';
import {
  challengeRuleSentence,
  challengeStatusLabel,
  localInputToIso,
  resolveUpdateStartAt,
  rewardStatusLabel,
  toLocalInput,
  validateRewardRows,
} from '../../../../utils/donate-challenge';
import { AdminButton, AdminCard, AdminPageHeader, AdminStatusPill } from '../../ui';
import type { AdminTone } from '../../ui';

// Admin tạo/sửa/huỷ "Sự kiện donate" — thử thách donate ở cấp toàn nền tảng (không gắn 1
// creator cụ thể). Mọi thao tác ghi cần quyền Donate.Manage + mã Authenticator (giống cấp
// cosmetic ở ManagementCosmetic).

const RARITY_LABEL: Record<string, string> = {
  Common: 'Cổ điển',
  Rare: 'Hiếm',
  Epic: 'Sử thi',
  Legendary: 'Huyền thoại',
  Mythic: 'Thần thoại',
};
const TYPE_LABEL: Record<string, string> = {
  Nametag: 'Khung tên',
  AvatarEffect: 'Khung avatar',
};
const MODE_LABEL: Record<ChallengeMode, string> = {
  Top: 'Đua top',
  Race: 'Về đích',
};

// Màu nhãn của util dùng chung (antd Tag) đổi sang tone nhãn admin.
const TONE_BY_COLOR: Record<string, AdminTone> = {
  blue: 'brand',
  green: 'success',
  red: 'danger',
  gold: 'warning',
};
const toneOf = (color: string): AdminTone => TONE_BY_COLOR[color] || 'neutral';

type StartMode = 'now' | 'schedule';

let rewardRowSeq = 0;
const nextRewardKey = () => {
  rewardRowSeq += 1;
  return rewardRowSeq;
};

interface RewardRowForm {
  key: number;
  rankFrom: number | null;
  rankTo: number | null;
  giftMode: 'none' | 'item';
  storeItemCode?: string;
  durationDays: number | null;
  minedCa: number | null;
}

const emptyRewardRow = (rankFrom = 1, rankTo = 1): RewardRowForm => ({
  key: nextRewardKey(),
  rankFrom,
  rankTo,
  giftMode: 'none',
  storeItemCode: undefined,
  durationDays: null,
  minedCa: null,
});

interface FormState {
  title: string;
  description: string;
  mode: ChallengeMode;
  targetAmount: number | null;
  winnerCount: number;
  startMode: StartMode;
  startLocal: string;
  endLocal: string;
  rewards: RewardRowForm[];
}

const emptyForm = (): FormState => ({
  title: '',
  description: '',
  mode: 'Top',
  targetAmount: null,
  winnerCount: 1,
  startMode: 'now',
  startLocal: '',
  endLocal: '',
  rewards: [emptyRewardRow()],
});

const askTotp = (message: string): string | null => {
  // eslint-disable-next-line no-alert
  const totp = window.prompt(message)?.trim();
  if (!totp || totp.length !== 6 || !/^\d{6}$/.test(totp)) {
    toast.error('Cần mã Authenticator 6 số để thực hiện thao tác này.');
    return null;
  }
  return totp;
};

const errMessage = (err: any, fallback: string): string => err?.response?.data?.message || fallback;

const ManagementDonateEvent = () => {
  const [events, setEvents] = useState<DonateChallenge[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const [items, setItems] = useState<StoreItem[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalStartMode, setOriginalStartMode] = useState<StartMode>('now');
  const [originalStartLocal, setOriginalStartLocal] = useState('');
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DonateChallenge | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const loadList = () => {
    setLoadingList(true);
    listDonateEvents()
      .then(setEvents)
      .catch((err) => toast.error(errMessage(err, 'Không tải được danh sách sự kiện donate')))
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    loadList();
    Promise.all([getGrantableItems('Nametag'), getGrantableItems('AvatarEffect')])
      .then(([a, b]) => setItems([...a, ...b].filter((i) => i.rarity !== 'Mythic')))
      .catch(() => setItems([]));
  }, []);

  const loadDetail = (id: string) => {
    setLoadingDetail(true);
    getDonateEvent(id)
      .then(setDetail)
      .catch((err) => toast.error(errMessage(err, 'Không tải được chi tiết sự kiện')))
      .finally(() => setLoadingDetail(false));
  };

  const openDetail = (id: string) => {
    setDetailId(id);
    setDetail(null);
    loadDetail(id);
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEditModal = (c: DonateChallenge) => {
    const startLocal = toLocalInput(c.startAt);
    setForm({
      title: c.title,
      description: c.description || '',
      mode: c.mode,
      targetAmount: c.targetAmount,
      winnerCount: c.winnerCount,
      startMode: 'schedule',
      startLocal,
      endLocal: toLocalInput(c.endAt),
      rewards:
        c.rewards.length > 0
          ? c.rewards.map((r) => ({
              key: nextRewardKey(),
              rankFrom: r.rankFrom,
              rankTo: r.rankTo,
              giftMode: r.storeItemCode ? 'item' : 'none',
              storeItemCode: r.storeItemCode || undefined,
              durationDays: r.durationDays,
              minedCa: r.minedCa,
            }))
          : [emptyRewardRow()],
    });
    setOriginalStartMode('schedule');
    setOriginalStartLocal(startLocal);
    setEditingId(c.id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const addRewardRow = () => {
    setForm((f) => {
      const last = f.rewards[f.rewards.length - 1];
      const from = last ? (last.rankTo || 0) + 1 : 1;
      return { ...f, rewards: [...f.rewards, emptyRewardRow(from, from)] };
    });
  };

  const removeRewardRow = (key: number) => {
    setForm((f) => ({ ...f, rewards: f.rewards.filter((r) => r.key !== key) }));
  };

  const updateRewardRow = (key: number, patch: Partial<RewardRowForm>) => {
    setForm((f) => ({
      ...f,
      rewards: f.rewards.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    }));
  };

  const handleSave = () => {
    if (saving) return;
    const title = form.title.trim();
    if (!title) {
      toast.error('Vui lòng nhập tên sự kiện.');
      return;
    }
    if (form.description.length > 300) {
      toast.error('Mô tả tối đa 300 ký tự.');
      return;
    }
    if (form.mode === 'Race' && (!form.targetAmount || form.targetAmount <= 0)) {
      toast.error('Mốc Ca phải lớn hơn 0.');
      return;
    }
    if (!form.winnerCount || form.winnerCount < 1 || form.winnerCount > 10) {
      toast.error('Số người thắng phải từ 1 đến 10.');
      return;
    }
    if (form.startMode === 'schedule' && !form.startLocal) {
      toast.error('Vui lòng chọn giờ bắt đầu.');
      return;
    }
    const endAt = localInputToIso(form.endLocal);
    if (!endAt) {
      toast.error('Vui lòng chọn giờ kết thúc.');
      return;
    }

    const rewards: RewardInput[] = form.rewards.map((r) => ({
      rankFrom: Number(r.rankFrom),
      rankTo: Number(r.rankTo),
      storeItemCode: r.giftMode === 'item' ? r.storeItemCode || null : null,
      durationDays: r.giftMode === 'item' ? r.durationDays : null,
      minedCa: r.minedCa,
    }));
    if (rewards.some((r) => r.storeItemCode && !r.durationDays)) {
      toast.error('Vui lòng nhập số ngày cho vật phẩm đã chọn.');
      return;
    }
    const rewardError = validateRewardRows(rewards);
    if (rewardError) {
      toast.error(rewardError);
      return;
    }

    const body: SavePlatformChallengeBody = {
      title,
      description: form.description.trim() || null,
      mode: form.mode,
      targetAmount: form.mode === 'Race' ? form.targetAmount : null,
      winnerCount: form.winnerCount,
      endAt,
      startAt: editingId
        ? resolveUpdateStartAt({
            startMode: form.startMode,
            startLocal: form.startLocal,
            originalStartMode,
            originalStartLocal,
          })
        : form.startMode === 'now'
        ? null
        : localInputToIso(form.startLocal),
      rewards,
    };

    const totp = askTotp(
      editingId
        ? 'Nhập mã Authenticator (6 số) để xác nhận sửa sự kiện donate:'
        : 'Nhập mã Authenticator (6 số) để xác nhận tạo sự kiện donate:'
    );
    if (!totp) return;

    setSaving(true);
    const request = editingId ? updateDonateEvent(editingId, body, totp) : createDonateEvent(body, totp);
    request
      .then(() => {
        toast.success(editingId ? 'Đã sửa sự kiện donate' : 'Đã tạo sự kiện donate');
        closeModal();
        loadList();
        if (detailId === editingId && editingId) loadDetail(editingId);
      })
      .catch((err) => toast.error(errMessage(err, 'Lưu sự kiện donate thất bại')))
      .finally(() => setSaving(false));
  };

  const handleCancelEvent = () => {
    if (!detail || cancelling) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Huỷ sự kiện donate này? Không thể hoàn tác.')) return;
    const totp = askTotp('Nhập mã Authenticator (6 số) để xác nhận huỷ sự kiện donate:');
    if (!totp) return;
    setCancelling(true);
    cancelDonateEvent(detail.id, totp)
      .then(() => {
        toast.success('Đã huỷ sự kiện donate');
        loadList();
        loadDetail(detail.id);
      })
      .catch((err) => toast.error(errMessage(err, 'Huỷ sự kiện donate thất bại')))
      .finally(() => setCancelling(false));
  };

  const handleRetryRewards = () => {
    if (!detail || retrying) return;
    const totp = askTotp('Nhập mã Authenticator (6 số) để xác nhận trao lại quà:');
    if (!totp) return;
    setRetrying(true);
    retryDonateEventRewards(detail.id, totp)
      .then((res) => {
        toast.success(`Đã trao lại quà cho ${res.reset} người thắng`);
        loadDetail(detail.id);
      })
      .catch((err) => toast.error(errMessage(err, 'Trao lại quà thất bại')))
      .finally(() => setRetrying(false));
  };

  const columns = [
    { title: 'Tên', dataIndex: 'title', key: 'title' },
    {
      title: 'Kiểu',
      dataIndex: 'mode',
      key: 'mode',
      render: (m: ChallengeMode) => MODE_LABEL[m] || m,
    },
    {
      title: 'Bắt đầu',
      dataIndex: 'startAt',
      key: 'startAt',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Kết thúc',
      dataIndex: 'endAt',
      key: 'endAt',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (s: DonateChallenge['status']) => {
        const m = challengeStatusLabel(s);
        return <AdminStatusPill tone={toneOf(m.color)}>{m.label}</AdminStatusPill>;
      },
    },
    {
      title: '',
      key: 'action',
      render: (_: any, r: DonateChallenge) => (
        <div className="flex items-center gap-2">
          <AdminButton variant="soft" size={32} onClick={() => openDetail(r.id)}>
            Chi tiết
          </AdminButton>
          <a
            href={`/su-kien/${r.id}`}
            target="_blank"
            rel="noreferrer"
            className="adm-link text-sm"
          >
            Trang công khai
          </a>
        </div>
      ),
    },
  ];

  const itemOptions = items.map((i) => ({
    value: i.code,
    label: `${i.name} (${TYPE_LABEL[i.type] || i.type}${i.rarity ? ` — ${RARITY_LABEL[i.rarity] || i.rarity}` : ''})`,
  }));

  const canEditDetail = detail?.status === 'Scheduled';
  const canCancelDetail = detail?.status === 'Scheduled' || detail?.status === 'Running';
  const canRetryDetail = (detail?.winners || []).some((w) => w.rewardStatus === 'Failed');

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Sự kiện donate"
        description="Các sự kiện donate theo thời gian, kèm kiểu và trạng thái."
        actions={
          <AdminButton elevated icon={<Plus size={18} />} onClick={openCreateModal}>
            Tạo sự kiện
          </AdminButton>
        }
      />

      <AdminCard flush>
      <Table
        scroll={{ x: 'max-content' }}
        rowKey={(r) => r.id}
        columns={columns}
        dataSource={events}
        loading={loadingList}
        pagination={false}
      />
      </AdminCard>

      {/* Modal tạo / sửa */}
      <Modal
        open={modalOpen}
        title={editingId ? 'Sửa sự kiện donate' : 'Tạo sự kiện donate'}
        onCancel={closeModal}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Lưu"
        cancelText="Huỷ"
        width={720}
        destroyOnClose
      >
        <div className="flex flex-col gap-3">
          <div>
            <div className="text-sm mb-1 dark:text-gray-300">Tên</div>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="VD: Đại tiệc donate cuối tuần"
            />
          </div>

          <div>
            <div className="text-sm mb-1 dark:text-gray-300">Mô tả (không bắt buộc)</div>
            <Input.TextArea
              value={form.description}
              maxLength={300}
              showCount
              rows={2}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div className="flex gap-6 flex-wrap">
            <div>
              <div className="text-sm mb-1 dark:text-gray-300">Kiểu</div>
              <Radio.Group
                value={form.mode}
                onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
              >
                <Radio value="Top">Đua top</Radio>
                <Radio value="Race">Về đích</Radio>
              </Radio.Group>
            </div>

            {form.mode === 'Race' && (
              <div>
                <div className="text-sm mb-1 dark:text-gray-300">Mốc Ca</div>
                <InputNumber
                  min={1}
                  value={form.targetAmount ?? undefined}
                  onChange={(v) => setForm((f) => ({ ...f, targetAmount: v ?? null }))}
                />
              </div>
            )}

            <div>
              <div className="text-sm mb-1 dark:text-gray-300">Số người thắng</div>
              <InputNumber
                min={1}
                max={10}
                value={form.winnerCount}
                onChange={(v) => setForm((f) => ({ ...f, winnerCount: v || 1 }))}
              />
            </div>
          </div>

          <div>
            <div className="text-sm mb-1 dark:text-gray-300">Bắt đầu</div>
            <Radio.Group
              value={form.startMode}
              onChange={(e) => setForm((f) => ({ ...f, startMode: e.target.value }))}
            >
              <Radio value="now">Ngay bây giờ</Radio>
              <Radio value="schedule">Hẹn giờ</Radio>
            </Radio.Group>
            {form.startMode === 'schedule' && (
              <Input
                type="datetime-local"
                className="mt-2"
                style={{ width: 260 }}
                value={form.startLocal}
                onChange={(e) => setForm((f) => ({ ...f, startLocal: e.target.value }))}
              />
            )}
          </div>

          <div>
            <div className="text-sm mb-1 dark:text-gray-300">Kết thúc</div>
            <Input
              type="datetime-local"
              style={{ width: 260 }}
              value={form.endLocal}
              onChange={(e) => setForm((f) => ({ ...f, endLocal: e.target.value }))}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold dark:text-white">Phần thưởng theo hạng</div>
              <AdminButton variant="soft" size={32} onClick={addRewardRow}>
                Thêm dòng
              </AdminButton>
            </div>
            <div className="flex flex-col gap-2">
              {form.rewards.map((row) => (
                <div key={row.key} className="flex items-end gap-2 flex-wrap border-b pb-2 dark:border-gray-700">
                  <div>
                    <div className="text-xs mb-1 dark:text-gray-400">Từ hạng</div>
                    <InputNumber
                      min={1}
                      style={{ width: 90 }}
                      value={row.rankFrom ?? undefined}
                      onChange={(v) => updateRewardRow(row.key, { rankFrom: v ?? null })}
                    />
                  </div>
                  <div>
                    <div className="text-xs mb-1 dark:text-gray-400">Đến hạng</div>
                    <InputNumber
                      min={1}
                      style={{ width: 90 }}
                      value={row.rankTo ?? undefined}
                      onChange={(v) => updateRewardRow(row.key, { rankTo: v ?? null })}
                    />
                  </div>
                  <div>
                    <div className="text-xs mb-1 dark:text-gray-400">Vật phẩm</div>
                    <Select
                      style={{ width: 220 }}
                      value={row.giftMode === 'item' ? row.storeItemCode : '__none__'}
                      options={[{ value: '__none__', label: 'Không tặng vật phẩm' }, ...itemOptions]}
                      onChange={(v) =>
                        updateRewardRow(row.key, {
                          giftMode: v === '__none__' ? 'none' : 'item',
                          storeItemCode: v === '__none__' ? undefined : v,
                        })
                      }
                    />
                  </div>
                  {row.giftMode === 'item' && (
                    <div>
                      <div className="text-xs mb-1 dark:text-gray-400">Số ngày</div>
                      <InputNumber
                        min={1}
                        max={365}
                        style={{ width: 90 }}
                        value={row.durationDays ?? undefined}
                        onChange={(v) => updateRewardRow(row.key, { durationDays: v ?? null })}
                      />
                    </div>
                  )}
                  <div>
                    <div className="text-xs mb-1 dark:text-gray-400">Ca đào</div>
                    <InputNumber
                      min={0}
                      style={{ width: 110 }}
                      value={row.minedCa ?? undefined}
                      onChange={(v) => updateRewardRow(row.key, { minedCa: v ?? null })}
                    />
                  </div>
                  {form.rewards.length > 1 && (
                    <AdminButton variant="danger-soft" size={32} onClick={() => removeRewardRow(row.key)}>
                      Xoá
                    </AdminButton>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Drawer chi tiết */}
      <Drawer
        open={!!detailId}
        onClose={closeDetail}
        title={detail?.title || 'Chi tiết sự kiện donate'}
        width={640}
      >
        {loadingDetail && <div className="dark:text-white">Đang tải...</div>}
        {!loadingDetail && detail && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <AdminStatusPill tone={toneOf(challengeStatusLabel(detail.status).color)}>
                {challengeStatusLabel(detail.status).label}
              </AdminStatusPill>
              <span className="text-sm dark:text-gray-300">
                {challengeRuleSentence(detail.mode, detail.targetAmount, detail.winnerCount)}
              </span>
            </div>
            {detail.description && <p className="text-sm dark:text-gray-300">{detail.description}</p>}
            <p className="text-sm dark:text-gray-400">
              Bắt đầu {dayjs(detail.startAt).format('DD/MM/YYYY HH:mm')} · Kết thúc{' '}
              {dayjs(detail.endAt).format('DD/MM/YYYY HH:mm')}
            </p>

            <div className="flex gap-2 flex-wrap">
              {canEditDetail && (
                <AdminButton variant="soft" size={36} onClick={() => openEditModal(detail)}>
                  Sửa
                </AdminButton>
              )}
              {canCancelDetail && (
                <AdminButton variant="danger" size={36} loading={cancelling} onClick={handleCancelEvent}>
                  Huỷ sự kiện
                </AdminButton>
              )}
              {canRetryDetail && (
                <AdminButton variant="outline" size={36} loading={retrying} onClick={handleRetryRewards}>
                  Trao lại quà lỗi
                </AdminButton>
              )}
            </div>

            <div>
              <div className="font-semibold mb-2 dark:text-white">Bảng xếp hạng (top 20)</div>
              <Table
                size="small"
                rowKey={(r: any) => r.rank}
                pagination={false}
                dataSource={detail.leaderboard.slice(0, 20)}
                columns={[
                  { title: 'Hạng', dataIndex: 'rank', key: 'rank', width: 60 },
                  { title: 'Tên', dataIndex: 'displayName', key: 'displayName' },
                  {
                    title: 'Số Ca',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (v: number) => v.toLocaleString('vi-VN'),
                  },
                ]}
              />
            </div>

            <div>
              <div className="font-semibold mb-2 dark:text-white">Người thắng</div>
              <Table
                size="small"
                rowKey={(r: any) => r.rank}
                pagination={false}
                dataSource={detail.winners}
                columns={[
                  { title: 'Hạng', dataIndex: 'rank', key: 'rank', width: 60 },
                  { title: 'Tên', dataIndex: 'displayName', key: 'displayName' },
                  {
                    title: 'Số Ca',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (v: number) => v.toLocaleString('vi-VN'),
                  },
                  {
                    title: 'Trạng thái trao quà',
                    key: 'rewardStatus',
                    render: (_: any, w: any) => {
                      const m = rewardStatusLabel(w.rewardStatus);
                      return (
                        <div>
                          <AdminStatusPill tone={toneOf(m.color)}>{m.label}</AdminStatusPill>
                          {w.rewardStatus === 'Failed' && w.rewardError && (
                            <div className="text-xs text-red-500 mt-1">{w.rewardError}</div>
                          )}
                        </div>
                      );
                    },
                  },
                ]}
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ManagementDonateEvent;
